import React, { useMemo, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tasksAtom, fetchTasksAtom, updateTaskAtom } from '../../../../../atoms/v0/taskAtoms';
import { projectsAtom, fetchProjectsAtom } from '../../../../../atoms/v0/projectAtoms';
import { globalTaskEditAtom } from '../../../../../atoms/v3/taskAtoms';
import { showUndoToastAtom } from '../../../../../atoms/v3/undoToastAtoms';
import { shouldTaskAppearOnDate } from '../../../../../utils/taskTimeUtils';
import { sortTasksByTime } from '../../../../../utils/taskSortUtils';
import { Task } from '../../../../../types/task';
import { ComponentTitle } from './ComponentTitle';
import ClockAreaFooter from './ClockAreaFooter';
import { TaskTimeRange } from '../../../../../atoms/v3/clockModeAtoms';

interface RecentTasksAreaProps {
  currentTime: Date;
  onEditArea?: () => void; // 可选的编辑区域回调
  timeRange?: TaskTimeRange; // 时间范围筛选
}

/**
 * 时钟模式最近待办事项组件
 * 显示1-3个未完成任务，简洁显示适合时钟模式
 */
export const RecentTasksArea: React.FC<RecentTasksAreaProps> = ({
  currentTime,
  onEditArea,
  timeRange = 'today-tomorrow' // 默认显示今天+明天
}) => {
  const { t } = useTranslation(['v3/clock', 'v3/tasks']);
  const navigate = useNavigate();
  const tasks = useAtomValue(tasksAtom);
  const projects = useAtomValue(projectsAtom);
  const fetchTasks = useSetAtom(fetchTasksAtom);
  const fetchProjects = useSetAtom(fetchProjectsAtom);
  const updateTask = useSetAtom(updateTaskAtom);
  const setGlobalTaskEdit = useSetAtom(globalTaskEditAtom);
  const showUndoToast = useSetAtom(showUndoToastAtom);

  // 确保任务和项目数据已加载
  useEffect(() => {
    if (tasks.length === 0) {
      fetchTasks();
    }
    if (projects.length === 0) {
      fetchProjects();
    }
  }, [tasks.length, projects.length, fetchTasks, fetchProjects]);

  // 创建项目映射
  const projectsMap = useMemo(() => {
    const map: Record<string, { id: string; title: string }> = {};
    projects.forEach(project => {
      map[project.id] = {
        id: project.id,
        title: project.title
      };
    });
    return map;
  }, [projects]);

  // 获取并排序未完成任务
  const recentTasks = useMemo(() => {
    // 筛选未完成任务
    const incompleteTasks = tasks.filter(task => !task.done);

    if (incompleteTasks.length === 0) {
      return [];
    }

    // 使用taskv2的排序逻辑进行基础排序
    const sortedTasks = sortTasksByTime(incompleteTasks);

    // 根据时间范围筛选任务
    let filteredTasks: Task[] = [];

    if (timeRange === 'all-incomplete') {
      // 显示所有未完成任务
      filteredTasks = sortedTasks;
    } else {
      // 按日期筛选
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // 分组（使用taskv2的时间判断逻辑）
      const yesterdayTasks = sortedTasks.filter(task => shouldTaskAppearOnDate(task, yesterday));
      const todayTasks = sortedTasks.filter(task => shouldTaskAppearOnDate(task, today));
      const tomorrowTasks = sortedTasks.filter(task => shouldTaskAppearOnDate(task, tomorrow));

      // 根据时间范围组合任务
      const prioritizedTasks: Task[] = [];

      switch (timeRange) {
        case 'today':
          prioritizedTasks.push(...todayTasks);
          break;
        case 'today-tomorrow':
          prioritizedTasks.push(...todayTasks);
          prioritizedTasks.push(...tomorrowTasks);
          break;
        case 'yesterday-today-tomorrow':
          prioritizedTasks.push(...todayTasks);
          prioritizedTasks.push(...yesterdayTasks);
          prioritizedTasks.push(...tomorrowTasks);
          break;
      }

      filteredTasks = prioritizedTasks;
    }

    // 添加项目信息并返回所有任务
    return filteredTasks.map(task => {
      // 为每个任务添加项目信息
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: projectsMap[task.projectId]
        };
      }
      return task;
    });
  }, [tasks, projectsMap, timeRange]);

  // 处理任务切换
  const handleToggleTask = async (taskId: string, completed: boolean) => {
    const taskToUpdate = tasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    try {
      await updateTask({
        id: taskId,
        done: completed
      });

      // 显示撤销 toast
      showUndoToast({
        taskId,
        taskTitle: taskToUpdate.title,
        isCompleted: completed,
        previousState: !completed
      });
    } catch (error) {
      console.error('时钟模式任务状态切换失败:', error);
    }
  };

  // 处理任务点击
  const handleTaskClick = (taskId: string) => {
    setGlobalTaskEdit({
      taskId: taskId,
      isOpen: true,
      triggerSource: 'hyperlink'
    });
  };

  // 处理导航到任务页面
  const handleNavigateToTasks = () => {
    navigate('/v3/taskv2');
  };

  // 如果没有任务，显示空状态
  if (recentTasks.length === 0) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center cursor-pointer" onClick={onEditArea}>
        <div className="text-2xl mb-2">📝</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('v3/clock:recentTasks.empty', '暂无待办事项')}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* 统一标题 */}
      {/* <ComponentTitle
        title={t('v3/clock:recentTasks.title', '最近待办')}
        rightButton={{
          icon: '→',
          onClick: handleNavigateToTasks,
          title: t('v3/tasks:navigation.goToTasks', '前往任务页面')
        }}
      /> */}

      {/* 任务列表 */}
      <div className="flex-1 space-y-0 overflow-y-auto px-[18px] py-[6px] max-h-full">
        {recentTasks.map((task, index) => (
          <div
            key={task.id}
            className="flex items-center space-x-2 p-0 rounded-lg transition-colors cursor-pointer"
            onClick={() => handleTaskClick(task.id)}
          >
            {/* 完成状态按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleTask(task.id, !task.done);
              }}
              className={`flex-shrink-0 w-[12px] h-[12px] rounded-full border-[1.5px] transition-colors ${
                task.done
                  ? 'bg-green-500 border-green-500'
                  : 'border-[var(--cm-comp-text-color)] opacity-50'
              }`}
            >
              {task.done && (
                <svg className="w-full h-full text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* 任务内容 */}
            <div className="flex-1 min-w-0 border-b border-[var(--cm-modal-border-color)] py-[6px] leading-none">
              <div className="flex items-center space-x-1">
                {/* 优先级指示器 */}
                {/* {task.vital && <span className="text-red-500 text-xs">🔴</span>}
                {task.urgent && <span className="text-orange-500 text-xs">🟠</span>} */}
                
                {/* 任务标题 */}
                <span className="text-[11px] opacity-75">
                  {task.title}
                </span>
              </div>
              
              {/* 项目名称 */}
              {task.project && (
                <div className="text-[9px] opacity-35 truncate mt-[3px]">
                  {task.project.title}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <ClockAreaFooter
        text={"待办事项"}
        icon={"📋"}
        onClick={onEditArea}
      />
    </div>
  );
};

export default RecentTasksArea;
